import React, { createContext, useState, useEffect } from 'react';

/**
 * Darkens a given hex color by a specified percentage.
 */
const darkenColor = (hex, percent = 10) => {
  if (!hex) return hex;
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  r = Math.max(0, Math.floor(r * (100 - percent) / 100));
  g = Math.max(0, Math.floor(g * (100 - percent) / 100));
  b = Math.max(0, Math.floor(b * (100 - percent) / 100));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Theme State
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  
  // Accent Color State
  const [userAccentColor, setUserAccentColor] = useState(() => {
    return localStorage.getItem('userAccentColor') || null;
  });

  const getEffectiveAccentColor = () => {
    if (userAccentColor) return userAccentColor;
    return theme === 'light' ? '#ff8c00' : '#a393f5';
  };

  const accentColor = getEffectiveAccentColor();
  const hoverHighlightColor = darkenColor(accentColor, 10);

  // Background State
  const [backgroundType, setBackgroundType] = useState(() => {
    return localStorage.getItem('backgroundType') || 'wave';
  });
  
  const [backgroundSolidColor, setBackgroundSolidColor] = useState(() => {
    return localStorage.getItem('backgroundSolidColor') || (theme === 'light' ? '#f5f5f5' : '#121212');
  });

  // Persistence and CSS Variables
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.body.className = theme === 'light' ? 'light-mode' : 'dark-mode';
    
    // Update CSS variables
    document.documentElement.style.setProperty('--accent-color', accentColor);
    document.documentElement.style.setProperty('--accent-hover', hoverHighlightColor);
    
    // We keep these for backward compatibility if any CSS file uses them
    document.documentElement.style.setProperty('--highlight-color-light', accentColor);
    document.documentElement.style.setProperty('--highlight-color-dark', accentColor);
    document.documentElement.style.setProperty('--highlight-hover-light', hoverHighlightColor);
    document.documentElement.style.setProperty('--highlight-hover-dark', hoverHighlightColor);
    
    if (userAccentColor) {
      localStorage.setItem('userAccentColor', userAccentColor);
    }
    
    localStorage.setItem('backgroundType', backgroundType);
    localStorage.setItem('backgroundSolidColor', backgroundSolidColor);
  }, [theme, accentColor, hoverHighlightColor, userAccentColor, backgroundType, backgroundSolidColor]);

  const setAccentColor = (color) => {
    setUserAccentColor(color);
  };

  return (
    <ThemeContext.Provider 
      value={{ 
        theme, 
        setTheme, 
        accentColor,
        setAccentColor,
        hoverHighlightColor,
        backgroundType,
        setBackgroundType,
        backgroundSolidColor,
        setBackgroundSolidColor
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
